import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useDisplayPrice } from "@majourneys/currency";
import { activityHrefString } from "../../activities/lib/activity-href";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { PewterCard } from "../../../shared/ui/pewter-card";
import type { AiMatch } from "../api/ai.api";
import type { ChatLang } from "../hooks/use-ai-chat";
import { stripHtml, truncateOnWord } from "../../../shared/lib/strip-html";
import { RADIUS, SOFT } from "./soft-tokens";
import { BEVEL_INSET_NAVY, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";
import { usePriceUnit } from "../lib/price-unit";
import { isStayItem } from "@majourneys/shared";

/**
 * A pick, opened without leaving the answer.
 *
 * A card in the chat used to be a plain link: pressing one threw the
 * conversation away and landed the guest on the activity page. Here it lifts
 * instead — the preview grows out of the card that was pressed and floats over
 * the thread, which stays legible behind it, and the map beside it keeps its
 * pins. The full page is still one press away.
 *
 * **Opt-in.** Surfaces that want it wrap their thread in `MatchPreviewProvider`;
 * everywhere else `MatchCard` stays the link it has always been. The Discover
 * panel opts in; the modal does not (yet).
 *
 * Every line here comes off the `AiMatch` the API already returned — photo,
 * name, subtitle, overview, category, city, price. Nothing about duration,
 * opening hours or group size is invented to fill the card out.
 */

interface PreviewApi {
  open: (match: AiMatch, rect: DOMRect) => void;
}

const MatchPreviewContext = createContext<PreviewApi | null>(null);

/** Null outside a provider, which is what keeps the card a link elsewhere. */
export function useMatchPreview(): PreviewApi | null {
  return useContext(MatchPreviewContext);
}

export function MatchPreviewProvider({
  lang,
  inTrip,
  onAdd,
  hostRef,
  children,
}: {
  lang: ChatLang;
  inTrip: Set<string>;
  onAdd: (id: string) => void;
  /** The positioned element the preview centres itself in. */
  hostRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const [match, setMatch] = useState<AiMatch | null>(null);
  // Where the pressed card sat relative to the middle of the panel — the
  // offset the preview grows out of.
  const [lift, setLift] = useState({ dx: 0, dy: 0 });

  const api = useMemo<PreviewApi>(
    () => ({
      open: (m, rect) => {
        const host = hostRef.current?.getBoundingClientRect();
        if (host) {
          setLift({
            dx: rect.left + rect.width / 2 - (host.left + host.width / 2),
            dy: rect.top + rect.height / 2 - (host.top + host.height / 2),
          });
        }
        setMatch(m);
      },
    }),
    [hostRef],
  );

  return (
    <MatchPreviewContext.Provider value={api}>
      {children}
      {match ? (
        <MatchPreviewCard
          match={match}
          lang={lang}
          lift={lift}
          inTrip={inTrip.has(match.id)}
          onAdd={() => onAdd(match.id)}
          onClose={() => setMatch(null)}
        />
      ) : null}
    </MatchPreviewContext.Provider>
  );
}

function MatchPreviewCard({
  match,
  lang,
  lift,
  inTrip,
  onAdd,
  onClose,
}: {
  match: AiMatch;
  lang: ChatLang;
  lift: { dx: number; dy: number };
  inTrip: boolean;
  onAdd: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const priceUnit = usePriceUnit(match);
  const bookable = match.mode !== "explore";
  const title = match.title ?? match.slug;
  const href = activityHrefString({
    lang,
    citySlug: match.citySlug,
    categorySlug: match.categorySlug,
    segment: match.segment,
    slug: match.slug,
  });
  const tags = [match.categoryName, match.cityName].filter(Boolean).join(" · ");
  const addLabel = inTrip
    ? t("ai_concierge.added_to_trip", { defaultValue: "Added to trip" })
    : t("ai_concierge.add_to_trip", { defaultValue: "Add to trip" });
  // Stored as HTML; printed as text, and only as much of it as a preview
  // should carry.
  const overview = truncateOnWord(stripHtml(match.overview), 320);
  const cta = bookable
    ? t("ai_concierge.reserve", { defaultValue: "Reserve" })
    : t("ai_concierge.view_place", { defaultValue: "View place" });

  return (
    <>
      <button
        type="button"
        aria-label={t("ai_concierge.preview_close", { defaultValue: "Close preview" })}
        onClick={onClose}
        className="absolute inset-0 z-30 cursor-default"
        style={{ background: "rgba(17,14,57,0.22)", animation: "mjPreviewScrim 0.28s ease-out both" }}
      />
      <div
        // Remounting per pick restarts the keyframe, which is what makes a
        // second card lift out of its own position rather than appear.
        key={match.id}
        role="dialog"
        aria-label={title}
        className="absolute left-1/2 top-1/2 z-40 w-[440px] max-w-[calc(100%-24px)]"
        style={{ animation: "mjPreviewLift 0.36s cubic-bezier(.2,.8,.2,1) both" }}
      >
        <PewterCard
          radius={RADIUS.card}
          fill={SOFT.surface}
          innerClassName="flex max-h-[min(620px,82vh)] min-h-0 flex-col p-4"
          style={{ boxShadow: "0 40px 90px -24px rgba(17,14,57,0.5)" }}
        >
          <div className="relative shrink-0">
            <div
              className="w-full overflow-hidden"
              style={{ height: 180, borderRadius: RADIUS.media, background: "#eceaf5" }}
            >
              {match.heroImageUrl ? (
                <ImageWithFallback
                  src={match.heroImageUrl}
                  alt=""
                  className="h-full w-full object-cover"
                  sizes="440px"
                />
              ) : (
                <div className="grid h-full w-full place-items-center">
                  <Icon name="ri-image-2-line" size={22} style={{ color: SOFT.body }} aria-hidden="true" />
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("ai_concierge.preview_close", { defaultValue: "Close preview" })}
              className="absolute end-2 top-2 grid h-9 w-9 place-items-center transition hover:bg-white"
              style={{
                borderRadius: RADIUS.tile,
                background: SOFT.controlFill,
                border: `1px solid ${SOFT.line}`,
                color: SOFT.ink,
              }}
            >
              <Icon name="ri-close-line" size={17} aria-hidden="true" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto pt-3 [scrollbar-width:thin] [scrollbar-color:rgba(17,14,57,0.18)_transparent]">
            {/* Three bands, in the order a reader needs them: what and where,
                the one-line pitch, then the longer description. Before this
                they were four paragraphs of the same weight and the longest
                one — the overview — flooded the card. */}
            {tags ? (
              <div
                className="text-[10.5px] font-bold uppercase"
                style={{ color: SOFT.body, letterSpacing: "0.08em" }}
              >
                {tags}
              </div>
            ) : null}
            <h3
              className="mt-1.5 text-[19px] font-extrabold leading-[24px] tracking-[-0.35px]"
              style={{ color: SOFT.ink }}
            >
              {title}
            </h3>
            {match.subtitle ? (
              <p className="mt-2 text-[13.5px] leading-[20px]" style={{ color: SOFT.ink }}>
                {match.subtitle}
              </p>
            ) : null}

            {/* The overview is stored as HTML, so it is stripped to text
                before it is printed — rendering it raw put a literal
                "<p><strong>" in front of the venue's own name. Trimmed to a
                readable paragraph too: the full field runs to several
                hundred words, which is the full page's job, not a preview's. */}
            {overview ? (
              <div className="mt-3 border-t pt-3" style={{ borderColor: SOFT.hairline }}>
                <p className="text-[12.5px] leading-[19px]" style={{ color: SOFT.body }}>
                  {overview}
                </p>
              </div>
            ) : null}

            <a
              href={href}
              className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold"
              style={{ color: SOFT.goldInk }}
            >
              {t("ai_concierge.open_full_page", { defaultValue: "Open the full page" })}
              <Icon name="ri-arrow-right-up-line" size={14} aria-hidden="true" />
            </a>
          </div>

          {/* The action bar never scrolls away: the point of the preview is
              that the decision sits one press from the answer. */}
          <div className="mt-3 shrink-0 border-t pt-3" style={{ borderColor: SOFT.hairline }}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                {/* No price claim on an explore row — "free to visit" is true
                    of a small minority of them. */}
                {bookable && match.priceMad != null ? (
                  <>
                    <div className="text-[15px] font-extrabold leading-none" style={{ color: SOFT.ink }}>
                      {displayPrice(match.priceMad)}
                      {priceUnit ? (
                        <span className="text-[12px] font-semibold" style={{ color: SOFT.body }}>
                          {` ${priceUnit}`}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-[11px]" style={{ color: SOFT.body }}>
                      {/* Stays, camps included, are paid online; the rest on the day. */}
                      {isStayItem(match.categoryName, match.segment, match.priceType)
                        ? t("stays.receipt.paid_label")
                        : t("ai_concierge.due_on_the_day", { defaultValue: "Total due on the day" })}
                    </div>
                  </>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onAdd}
                aria-pressed={inTrip}
                aria-label={addLabel}
                title={addLabel}
                className="grid h-10 w-10 shrink-0 place-items-center transition"
                style={{
                  borderRadius: RADIUS.tile,
                  background: inTrip ? "#EAF3DE" : SOFT.controlFill,
                  border: inTrip ? "1px solid #C0DD97" : `1px solid ${SOFT.controlEdge}`,
                  color: inTrip ? "#3B6D11" : SOFT.ink,
                }}
              >
                <Icon name={inTrip ? "ri-check-line" : "ri-add-line"} size={18} aria-hidden="true" />
              </button>
              <a
                href={href}
                className="shrink-0 rounded-full px-5 py-2.5 text-[13.5px] font-semibold text-white"
                style={{
                  background: BEVEL_NAVY_FACE,
                  boxShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${BEVEL_INSET_NAVY}`,
                }}
              >
                {cta}
              </a>
            </div>
          </div>
        </PewterCard>
      </div>

      <style>{`
        @keyframes mjPreviewLift {
          from {
            transform: translate(calc(-50% + ${lift.dx}px), calc(-50% + ${lift.dy}px)) scale(0.42);
            opacity: 0;
          }
          to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
        @keyframes mjPreviewScrim { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          @keyframes mjPreviewLift {
            from { transform: translate(-50%, -50%) scale(1); opacity: 0; }
            to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          }
        }
      `}</style>
    </>
  );
}
